import type { ReactChild } from "react";
import { createContext, useCallback, useMemo, useState } from "react";

import type { ProductDataFragment } from "../shopify-graphql/types";

export interface CartContextInterface {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  itemModalState: ItemModalState;
  openItemModal: (params: {
    product: ProductDataFragment;
    isBuyingNow: boolean;
  }) => void;
  closeItemModal: () => void;
  isCurrentOptionAvailable: boolean;
  setIsCurrentOptionAvailable: (isAvailable: boolean) => void;
}

export const CartContext = createContext<CartContextInterface>({
  isCartOpen: false,
  openCart: () => undefined,
  closeCart: () => undefined,
  toggleCart: () => undefined,
  itemModalState: { open: false },
  openItemModal: () => undefined,
  closeItemModal: () => undefined,
  isCurrentOptionAvailable: true,
  setIsCurrentOptionAvailable: () => undefined,
});

export type ItemModalState =
  | {
      open: true;
      product: ProductDataFragment;
      initialVariantId: string | undefined;
    }
  | {
      open: false;
      product?: ProductDataFragment | undefined;
      initialVariantId?: string | undefined;
    };

export default function CartUIProvider({ children }: { children: ReactChild }) {
  const [open, setOpen] = useState(false);

  const [isCurrentOptionAvailable, setIsCurrentOptionAvailable] =
    useState(true);

  const [itemModalState, setItemModalState] = useState<ItemModalState>({
    open: false,
  });

  const openCart = useCallback(() => {
    setOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setOpen(false);
  }, []);

  const toggleCart = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const openItemModal = useCallback(
    (params: { product: ProductDataFragment }) => {
      setItemModalState({
        open: true,
        product: params.product,
        initialVariantId: params.product.variants.edges[0]?.node.id,
      });
    },
    [],
  );

  const closeItemModal = useCallback(() => {
    setItemModalState((prev) => ({
      open: false,
      product: prev.product,
      initialVariantId: prev.initialVariantId,
    }));
  }, []);

  const contextValue = useMemo(() => {
    return {
      isCartOpen: open,
      openCart,
      closeCart,
      toggleCart,
      itemModalState,
      openItemModal,
      closeItemModal,
      isCurrentOptionAvailable,
      setIsCurrentOptionAvailable,
    };
  }, [
    open,
    openCart,
    closeCart,
    toggleCart,
    openItemModal,
    closeItemModal,
    itemModalState,
    isCurrentOptionAvailable,
    setIsCurrentOptionAvailable,
  ]);

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}
